import { FunctionComponent, ReactElement, useEffect, useRef, useState } from 'react';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import {
	CategoryScale,
	Chart as ChartJS,
	Chart,
	ChartEvent,
	Legend,
	LinearScale,
	LineElement,
	PointElement,
	Title,
	TimeScale,
	Tooltip,
	TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box } from '@mui/material';
import { ChartViewType, AllCyclesResponse } from '@/api/dashboard/types';
import Backdrop from '@/components/ui/backdrop';
import { QueryDateRange } from '../../../types';
import { chartBackgroundPlugin } from './plugins';
import CompletedCyclesChartTitle from './CompletedCyclesChartTitle';
import { useCompletedCyclesChartData } from './useCompletedCyclesChartData';
import { formatTooltipTitle } from './utils';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	TimeScale,
	LineElement,
	Title,
	Tooltip,
	Legend
);

const colors = ['#57BC8D', '#BBF9DC'];
interface CompletedCyclesChartProps {
	completedCycles?: AllCyclesResponse;
	legendLabel?: string;
	dateRange?: QueryDateRange;
	exportType: string | null;
	onChartReady?: (ready: boolean) => void;
	isLoading: boolean;
}

const CompletedCyclesChart: FunctionComponent<CompletedCyclesChartProps> = ({
	completedCycles,
	dateRange,
	exportType,
	onChartReady,
	isLoading,
}): ReactElement => {
	const {
		numberOfCompletedCycles: totalCompletedCycles,
		numberOfCompletedCyclesPreviousPeriod: previousCompletedCycles,
		percentDifference,
		view,
	} = completedCycles || {};
	const { labels, dataset } = useCompletedCyclesChartData(completedCycles, dateRange);

	const chartRef = useRef(null);
	const [chartImgSrc, setChartImgSrc] = useState<string | null>(null);
	const [imageLoaded, setImageLoaded] = useState<boolean>(false);
	const [readySent, setReadySent] = useState(false);

	useEffect(() => {
		if (chartImgSrc && imageLoaded && !readySent) {
			onChartReady?.(true);
			setReadySent(true);
		}
	}, [chartImgSrc, imageLoaded, onChartReady, readySent]);

	useEffect(() => {
		if (!exportType) {
			setReadySent(false);
		}
	}, [exportType]);

	const convertToImgSource = (blob: Blob | null) => {
		if (!blob) return;
		const url = URL.createObjectURL(blob);
		setChartImgSrc(url);
	};

	const loadChartImageUrl = () => {
		const chart = chartRef.current as unknown as Chart;
		const { canvas, ctx } = chart;

		ctx.globalCompositeOperation = 'destination-over';
		ctx.fillStyle = '#fff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.canvas.toBlob(convertToImgSource);
	};

	const afterPrintHandler = () => {
		// In rear cases, simulate with CPU throttle 6x, the chart won't have time to automatically update.
		setTimeout(() => {
			(chartRef.current as unknown as Chart)?.update();
		}, 100);
	};

	useEffect(() => {
		window.addEventListener('afterprint', afterPrintHandler);
		return () => window.removeEventListener('afterprint', afterPrintHandler);
	}, []);

	useEffect(() => {
		if (exportType) {
			loadChartImageUrl();
		} else {
			setChartImgSrc(null);
			setImageLoaded(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exportType]);

	const handleVisibilityChange = () => {
		if (!chartRef) return;
		(chartRef.current as unknown as Chart)?.render();
	};

	// This is logic is added beacuse changing browser tab in chrome makes chart disappear
	useEffect(() => {
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, []);

	return (
		<Box sx={{ position: 'relative' }}>
			{isLoading && (
				<Backdrop
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(255, 255, 255, 1)',
					}}
				/>
			)}
			<CompletedCyclesChartTitle
				numberOfCompletedCycles={totalCompletedCycles || 0}
				percentDifference={percentDifference || 0}
				previousCompletedCycles={previousCompletedCycles || 0}
				sx={{ displayPrint: 'none' }}
			/>
			<Box
				component="div"
				sx={{ height: '293px', width: '100%', displayPrint: 'none', position: 'relative' }}
			>
				<Line
					aria-label="Completed cycles chart"
					ref={chartRef}
					plugins={[chartBackgroundPlugin]}
					options={{
						maintainAspectRatio: false,
						responsive: true,
						layout: { padding: 20 },
						plugins: {
							legend: {
								position: 'top',
								align: 'end',
								onClick: (event: ChartEvent) => event.native?.stopPropagation(),
								labels: {
									boxHeight: 1,
									boxWidth: 20,
									filter: (item) => !!item.text,
								},
							},
							tooltip: {
								callbacks: {
									title: (tooltipItems: TooltipItem<'line'>[]) =>
										formatTooltipTitle(tooltipItems, dateRange),
								},
							},
						},
						scales: {
							x: {
								offset: true,
								ticks: {
									maxRotation: view === ChartViewType.Hours ? 45 : 50,
									minRotation: view === ChartViewType.Hours ? 45 : 0,
								},
							},
							y: {
								beginAtZero: true,
								ticks: {
									precision: 0,
								},
							},
						},
					}}
					/* eslint-disable */
					data={{
						labels: labels?.map(({ text }) => text) || [],
						datasets: dataset
							? dataset.map((series, index) => ({
								label: series.name,
								data: series.datapoints || [],
								backgroundColor: colors[index % colors.length],
								borderColor: colors[index % colors.length],
							}))
							: [],
					}}
				/* eslint-enable */
				/>
			</Box>
			<Box
				component="div"
				sx={{
					position: 'relative',
					width: '100%',
					display: 'none',
					displayPrint: 'block',
				}}
			>
				<CompletedCyclesChartTitle numberOfCompletedCycles={totalCompletedCycles || 0} />
				<img
					onLoad={() => setImageLoaded(true)}
					src={chartImgSrc || ''}
					alt="chart"
					className="chart-image"
				/>
			</Box>
		</Box>
	);
};

export default CompletedCyclesChart;
